import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  Drafts as DraftsIcon,
  MarkEmailRead as MarkReadIcon,
  FilterList as FilterIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  Chat as ChatBubbleIcon,
} from '@mui/icons-material';
import { notificationsAPI, type Notification, type NotificationType } from '../services/api';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0); // 0 = все, 1 = непрочитанные
  const [filterType, setFilterType] = useState<NotificationType | 'all'>('all');
  const [categoryTab, setCategoryTab] = useState<'events' | 'blog'>('events');

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAPI.getNotifications(100, 0, false);
      setNotifications(data.notifications);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleMarkAsRead = async () => {
    try {
      await notificationsAPI.markAsRead({ mark_all: true });
      loadNotifications();
    } catch (error) {
      console.error('Ошибка отметки уведомлений:', error);
    }
  };

  const handleMarkSingleAsRead = async (notificationId: number) => {
    try {
      await notificationsAPI.markAsRead({ notification_ids: [notificationId] });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Ошибка отметки уведомления:', error);
    }
  };

  const handleMarkAsUnread = async (notificationId: number) => {
    try {
      await notificationsAPI.markAsUnread([notificationId]);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n)
      );
    } catch (error) {
      console.error('Ошибка:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Ошибка удаления уведомления:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkSingleAsRead(notification.id);
    }

    if (notification.link) {
      navigate(notification.link);
    } else if (notification.event_id) {
      navigate(`/events/${notification.event_id}`);
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'event_created':
      case 'event_updated':
        return <EventIcon />;
      case 'event_cancelled':
        return <CancelIcon />;
      case 'application_approved':
        return <CheckCircleIcon />;
      case 'application_rejected':
      case 'application_received':
        return <AccessTimeIcon />;
      case 'new_participant':
      case 'participant_left':
        return <PeopleIcon />;
      case 'free_slots':
        return <NotificationsIcon />;
      case 'reminder':
        return <CampaignIcon />;
      case 'blog_article':
        return <ArticleIcon />;
      case 'blog_comment':
        return <ChatBubbleIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getAvatarColor = (type: NotificationType) => {
    switch (type) {
      case 'event_created':
      case 'event_updated':
        return '#2196f3';
      case 'event_cancelled':
        return '#f44336';
      case 'application_approved':
        return '#4caf50';
      case 'application_rejected':
      case 'application_received':
        return '#ff9800';
      case 'new_participant':
      case 'participant_left':
        return '#9c27b0';
      case 'free_slots':
        return '#00bcd4';
      case 'reminder':
        return '#ff5722';
      case 'blog_article':
        return '#3f51b5';
      case 'blog_comment':
        return '#009688';
      default:
        return '#757575';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'только что';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} мин. назад`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ч. назад`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} дн. назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  };

  const getNotificationTitle = (notification: Notification) => {
    const titles: Record<NotificationType, string> = {
      event_created: '🎉 Новое мероприятие',
      event_updated: '📝 Обновление мероприятия',
      event_cancelled: '❌ Мероприятие отменено',
      application_approved: '✅ Заявка одобрена',
      application_rejected: '❌ Заявка отклонена',
      application_received: '📨 Новая заявка',
      new_participant: '👥 Новый участник',
      participant_left: '👤 Участник покинул мероприятие',
      free_slots: '🎟️ Появились свободные места',
      reminder: '📢 Напоминание',
      blog_article: '📝 Новая статья',
      blog_comment: '💬 Новый комментарий',
    };
    return titles[notification.type] || notification.title;
  };

  const blogTypes = ['blog_article', 'blog_comment'];

  // Category-filtered notifications
  const categoryFiltered = notifications.filter(n =>
    categoryTab === 'blog' ? blogTypes.includes(n.type) : !blogTypes.includes(n.type)
  );

  // Unread counts per category
  const categoryUnreadCount = categoryFiltered.filter(n => !n.is_read).length;
  const categoryTotalCount = categoryFiltered.length;

  // Final filtered list (category + read status + type filter)
  const filteredNotifications = categoryFiltered
    .filter(n => tabValue === 0 ? true : !n.is_read)
    .filter(n => filterType === 'all' ? true : n.type === filterType);

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap',      // ← Позволяет перенос на новую строку
          gap: { xs: 1, sm: 2 },  // ← Отступ между элементами
          minWidth: '100%'  // ← Гарантия, что контейнер занимает всю ширину
        }}>
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '1.375rem', sm: '2.125rem' } // ← Меньше шрифт на мобильном
            }}
          >
            🔔 Уведомления
          </Typography>
          <Button
            variant="outlined"
            startIcon={<MarkReadIcon />}
            onClick={handleMarkAsRead}
            disabled={notifications.every(n => n.is_read)}
            sx={{
              minWidth: 'auto', // ← Убираем фикс. ширину кнопки
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Прочитать все
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Прочитать
            </Box>
          </Button>
        </Box>

        {/* Категория: Мероприятия / Блог */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', mb: 1 }}>
          <Box onClick={() => { setCategoryTab('events'); setFilterType('all'); }}
            sx={{
              flex: 1, py: 1, textAlign: 'center', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
              color: categoryTab === 'events' ? '#1565c0' : '#64748b',
              borderBottom: categoryTab === 'events' ? '2px solid #1565c0' : '2px solid transparent',
              '&:hover': { bgcolor: '#f5f7fa' },
            }}>
            Мероприятия
          </Box>
          <Box onClick={() => { setCategoryTab('blog'); setFilterType('all'); }}
            sx={{
              flex: 1, py: 1, textAlign: 'center', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
              color: categoryTab === 'blog' ? '#1565c0' : '#64748b',
              borderBottom: categoryTab === 'blog' ? '2px solid #1565c0' : '2px solid transparent',
              '&:hover': { bgcolor: '#f5f7fa' },
            }}>
            Блог
          </Box>
        </Box>

        {/* Табы */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={`Все (${categoryTotalCount})`} />
            <Tab label={`Непрочитанные (${categoryUnreadCount})`} />
          </Tabs>
        </Box>
      </Box>

      {/* Фильтр по типу */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Тип уведомления</InputLabel>
            <Select
              value={filterType}
              label="Тип уведомления"
              onChange={(e) => setFilterType(e.target.value as NotificationType | 'all')}
            >
              <MenuItem value="all">Все типы</MenuItem>
              {categoryTab === 'events' ? (
                <>
                  <MenuItem value="event_created">Создание мероприятия</MenuItem>
                  <MenuItem value="event_updated">Обновление мероприятия</MenuItem>
                  <MenuItem value="event_cancelled">Отмена мероприятия</MenuItem>
                  <MenuItem value="application_approved">Одобрение заявки</MenuItem>
                  <MenuItem value="application_rejected">Отклонение заявки</MenuItem>
                  <MenuItem value="application_received">Новая заявка</MenuItem>
                  <MenuItem value="new_participant">Новый участник</MenuItem>
                  <MenuItem value="participant_left">Участник покинул</MenuItem>
                  <MenuItem value="free_slots">Свободные места</MenuItem>
                  <MenuItem value="reminder">Напоминание</MenuItem>
                </>
              ) : (
                <>
                  <MenuItem value="blog_article">Новая статья</MenuItem>
                  <MenuItem value="blog_comment">Новый комментарий</MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Список уведомлений */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Нет уведомлений
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabValue === 1
                ? 'У вас нет непрочитанных уведомлений'
                : 'Уведомления появятся здесь'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                    py: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(notification.type),
                        width: 48,
                        height: 48,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body1"
                          fontWeight={notification.is_read ? 'normal' : 'bold'}
                        >
                          {getNotificationTitle(notification)}
                        </Typography>
                        {!notification.is_read && (
                          <Chip
                            label="Новое"
                            size="small"
                            color="primary"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color: notification.is_read ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {formatTimeAgo(notification.created_at)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!notification.is_read ? (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleMarkSingleAsRead(notification.id); }}
                          title="Отметить как прочитанное"
                          sx={{ color: '#1565c0' }}
                        >
                          <MarkReadIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsUnread(notification.id); }}
                          title="Отметить как непрочитанное"
                          sx={{ color: '#ef6c00' }}
                        >
                          <DraftsIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                        title="Удалить"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
